import { motion } from "framer-motion";
import whoweareData from "~/data/about-us/whoweare.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { cn } from "~/lib/utils";

const HeroIntroduction = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const titleVariants = {
    hidden: { opacity: 0, y: -40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="relative w-full mb-5 overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        <motion.div variants={titleVariants} className="text-center mb-8 md:mb-12">
          <motion.h1
            initial={{ scale: 0.8, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={cn("text-4xl md:text-5xl font-bold text-textColor mb-6", medium)}
          >
            Kundaldham Activities
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="w-32 h-1.5 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-8"
          />

          {/* Introduction text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className={cn("text-lg md:text-xl text-justify text-textSecondaryColor max-w-4xl mx-auto leading-relaxed", normalInter)}
          >
            {whoweareData.content.introduction}
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Timeline Block Component for alternating layout
const TimelineSection = ({ section, index }: { section: (typeof whoweareData.content.sections)[0]; index: number }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();
  const isEven = index % 2 === 0;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, x: isEven ? -40 : 40 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.1 },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: isEven ? 40 : -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.2 },
    },
  };

  return (
    <motion.div ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} variants={containerVariants} className="mb-16 md:mb-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Timeline number and connecting line */}
        <div className="hidden md:flex md:col-span-2 items-center justify-between mb-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-primaryColor to-primaryColor/80 flex items-center justify-center text-white font-bold text-lg shadow-lg border-4 border-white"
          >
            {index + 1}
          </motion.div>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex-grow h-1 bg-gradient-to-r from-primaryColor/30 to-primaryColor/10 origin-left"
          />
        </div>

        {/* Image and Content - using flex direction to control order */}
        <div className={cn("flex flex-col md:flex-row gap-8 md:gap-12 md:col-span-2 items-center", isEven ? "md:flex-row-reverse" : "")}>
          {/* Image Section */}
          <motion.div
            variants={imageVariants}
            className="relative overflow-hidden rounded-2xl shadow-xl border-2 border-primaryColor/20 flex-shrink-0 w-full md:w-1/2"
          >
            <div className="relative aspect-video md:aspect-square overflow-hidden rounded-xl">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                src={section.imageSrc}
                alt={section.imageAlt}
                className="w-full h-full object-cover"
              />
              <motion.div
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-gradient-to-t from-primaryColor/40 to-transparent"
              />
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div variants={contentVariants} className="w-full md:w-1/2 flex flex-col justify-center">
            {/* Section number for mobile */}
            {/* <motion.div
              initial={{ opacity: 0 }}
              animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="md:hidden mb-3 inline-flex"
            >
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primaryColor to-primaryColor/80 text-white font-bold text-sm flex items-center justify-center">
                {index + 1}
              </span>
            </motion.div> */}

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}
            >
              {section.title}
            </motion.h2>

            {/* Decorative line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="origin-left w-16 h-1.5 bg-gradient-to-r from-primaryColor to-primaryColor/50 rounded-full mb-6"
            />

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className={cn("text-base md:text-lg text-textSecondaryColor leading-relaxed mb-6", normalInter)}
            >
              {section.content}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
function OurActivities() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.div className="w-full">
      {/* Hero Section */}
      <HeroIntroduction />

      {/* Timeline Activities Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 mb-16 md:mb-28"
      >
        <motion.div className="space-y-12 md:space-y-20">
          {whoweareData.content.sections.map((section, index) => (
            <TimelineSection key={index} section={section} index={index} />
          ))}
        </motion.div>
      </motion.section>

      {/* Closing Section */}
      {/* <ClosingSection /> */}
    </motion.div>
  );
}

export default OurActivities;
